/**
 * The fountain court (lifted from lantern-water's `world.js` `createTown`,
 * the circular paving rosette, and `carvedFountain`): a stone ring and paved
 * disc, three rings of forty individually cut stones, and the carved fountain:
 * closed lathe profiles for the basin, the stem and the upper bowl, a rim, ten
 * relief medallions, a brass finial, rippling water in both bowls and twelve
 * falling streams. Origin at the fountain's centre (the prototype's -6, 1.5).
 * No params. The paved disc's walking surface, the basin's collider and the
 * wish are the scene record's.
 *
 * The water's ripples are measured from the fountain's own centre: each water
 * vertex carries its position in the fountain's frame (`abFountain`), so the
 * shader is the same wherever the court stands and after static batching
 * (the prototype passed its world centre as a uniform). Time is the kit's
 * shared `breezeTime`, as the prototype's.
 */
import * as THREE from 'three';
import { addShaderHook } from '../../material-hooks.ts';
import type { Prefab } from './types.ts';

const waterFragment = `
vec2 fp=abFountainLocal.xz;
float rings=0.;
if(abFountainLocal.y<1.){
  for(int i=0;i<12;i++){
    float a=float(i)*6.2831853/12.;
    float d=length(fp-vec2(cos(a),sin(a))*1.18);
    float wave=fract(d*5.-fountainTime*1.1+float(i)*.13);
    rings+=smoothstep(.92,1.,wave)*(1.-smoothstep(.06,.37,d));
  }
} else rings=smoothstep(.9,1.,fract(length(fp)*8.-fountainTime*.8))*.3;
float caustic=sin(fp.x*21.+fountainTime)*sin(fp.y*19.-fountainTime*.73);
diffuseColor.rgb*=.87+caustic*.06;
diffuseColor.rgb=mix(diffuseColor.rgb,vec3(.38,.62,.66),min(.55,rings*.45));`;

const streamFragment = `
float flowing=sin(streamUV.x*83.-fountainTime*10.);
diffuseColor.rgb*=.65+.35*flowing*flowing;`;

export const prefab: Prefab = {
  id: 'fountain_court',
  buildPrefab(ctx) {
    const root = new THREE.Group();
    const { cylinder, mesh, m, kit } = ctx;
    // ---- the rosette
    cylinder(root, 0, 0.018, 0, 2.8, 0.06, m.stone);
    cylinder(root, 0, 0.06, 0, 2.62, 0.06, m.paving);
    for (let ring = 0; ring < 3; ring += 1) {
      for (let i = 0; i < 40; i += 1) {
        const a = (i / 40) * Math.PI * 2 + 0.004;
        const b = ((i + 1) / 40) * Math.PI * 2 - 0.004;
        const r0 = 1.68 + ring * 0.31;
        const r1 = r0 + 0.285;
        const y = 0.101;
        const points = [Math.cos(a) * r0, y, Math.sin(a) * r0, Math.cos(b) * r0, y, Math.sin(b) * r0, Math.cos(b) * r1, y, Math.sin(b) * r1, Math.cos(a) * r1, y, Math.sin(a) * r1];
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
        geo.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 1, 0, 1, 1, 0, 1], 2));
        geo.setIndex([0, 2, 1, 0, 3, 2]);
        geo.computeVertexNormals();
        mesh(root, geo, (i + ring) % 5 ? m.stone : m.cream, 0, 0, 0);
      }
    }
    // ---- the carved fountain
    const lathe = (profile: readonly (readonly [number, number])[], material: THREE.Material = m.limestone): THREE.Mesh =>
      mesh(root, new THREE.LatheGeometry(profile.map(([r, y]) => new THREE.Vector2(r, y)), 40), material, 0, 0, 0);
    lathe([
      [0.3, 0.12],
      [1.3, 0.12],
      [1.51, 0.18],
      [1.59, 0.39],
      [1.65, 0.48],
      [1.65, 0.6],
      [1.51, 0.64],
      [1.43, 0.54],
      [1.39, 0.3],
      [0.3, 0.3],
      [0.3, 0.12],
    ]);
    const rim = mesh(root, new THREE.TorusGeometry(1.58, 0.045, 6, 40), m.cream, 0, 0.59, 0);
    rim.rotation.x = -Math.PI / 2;
    lathe(
      [
        [0.31, 0.43],
        [0.34, 0.48],
        [0.34, 0.58],
        [0.25, 0.62],
        [0.21, 1.1],
        [0.28, 1.23],
        [0.28, 1.32],
        [0.2, 1.37],
        [0.2, 1.49],
      ],
      m.stone,
    );
    lathe([
      [0.14, 1.43],
      [0.4, 1.44],
      [0.65, 1.55],
      [0.81, 1.72],
      [0.81, 1.79],
      [0.71, 1.8],
      [0.64, 1.68],
      [0.45, 1.61],
      [0.14, 1.61],
      [0.14, 1.43],
    ]);
    cylinder(root, 0, 1.86, 0, 0.13, 0.34, m.stone, 0.75);
    mesh(root, ctx.sphere, m.brass, 0, 2.12, 0, 0.16, 0.25, 0.16);
    cylinder(root, 0, 1.99, 0, 0.2, 0.065, m.cream);
    const medallion = kit.shared('fountain:medallion', () => new THREE.TorusGeometry(0.073, 0.018, 4, 10));
    for (let i = 0; i < 10; i += 1) {
      const a = (i * Math.PI) / 5;
      const ornament = new THREE.Group();
      ornament.position.set(Math.sin(a) * 1.595, 0.39, Math.cos(a) * 1.595);
      ornament.rotation.y = a;
      root.add(ornament);
      mesh(ornament, medallion, m.cream, 0, 0, 0);
      mesh(ornament, ctx.sphere, m.stone, 0, 0, 0.012, 0.041, 0.041, 0.026);
    }
    const time = { fountainTime: kit.uniforms.breezeTime };
    const water = kit.shared('fountain:water', () => {
      const material = new THREE.MeshStandardMaterial({ color: '#527c85', roughness: 0.28, metalness: 0.16 });
      addShaderHook(material, {
        key: 'town-fountain-ripples',
        uniforms: time,
        vertexHeader: 'attribute vec3 abFountain;varying vec3 abFountainLocal;',
        fragmentHeader: 'uniform float fountainTime;varying vec3 abFountainLocal;',
        vertex: [{ chunk: 'begin_vertex', after: 'abFountainLocal=abFountain;' }],
        fragment: [{ chunk: 'color_fragment', after: waterFragment }],
      });
      return material;
    });
    for (const [radius, y] of [
      [1.445, 0.505],
      [0.685, 1.725],
    ] as const) {
      const g = new THREE.CircleGeometry(radius, 40);
      g.rotateX(-Math.PI / 2);
      g.translate(0, y, 0);
      // The surface's points in the fountain's frame (x, height, z), for the ripples.
      g.setAttribute('abFountain', new THREE.Float32BufferAttribute(Array.from(g.getAttribute('position').array), 3));
      const surface = mesh(root, g, water, 0, 0, 0);
      surface.castShadow = false;
    }
    const stream = kit.shared('fountain:streams', () => {
      const material = new THREE.MeshStandardMaterial({ color: '#b9dade', roughness: 0.26, metalness: 0.12 });
      addShaderHook(material, {
        key: 'town-fountain-streams',
        uniforms: time,
        vertexHeader: 'varying vec2 streamUV;',
        fragmentHeader: 'uniform float fountainTime;varying vec2 streamUV;',
        vertex: [{ chunk: 'begin_vertex', after: 'streamUV=uv;' }],
        fragment: [{ chunk: 'color_fragment', after: streamFragment }],
      });
      return material;
    });
    for (let i = 0; i < 12; i += 1) {
      const a = (i * Math.PI) / 6;
      const at = (radius: number, y: number): THREE.Vector3 => new THREE.Vector3(Math.cos(a) * radius, y, Math.sin(a) * radius);
      const curve = new THREE.QuadraticBezierCurve3(at(0.78, 1.765), at(1.25, 1.79), at(1.18, 0.508));
      const s = mesh(root, new THREE.TubeGeometry(curve, 18, 0.011, 4, false), stream, 0, 0, 0);
      s.castShadow = false;
    }
    return { root };
  },
};
